import { Injectable } from '@angular/core';
import { selectInspiredPickViewModel } from '@app/order/selectors/inspired-picks/inspired-picks.selectors';
import { Store } from '@ngrx/store';
import { PanAppState, Tracking } from '@panamax/app-state';
import { FEATURES } from '@shared/constants/splitio-features';
import { combineLatest, Observable, of } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { DyfViewModel } from 'src/app/order/models/dyf-view-model';
import {
  selectDYFProductNumbers,
  selectDYFViewModel,
} from 'src/app/order/selectors/dyf/dyf.selectors';
import { NavigationHelperService } from 'src/app/shared/helpers/navigation.helpers.service';
import { OrderItemService } from 'src/app/shared/services/order-item.service';
import { DidYouForgetAnalyticsService } from '../tracking/analytics/did-you-forget-analytics.service';
import { TrackingTypeEnum } from '../tracking/shared/models/tracking-type-enum';
import { DidYouForgetTracingService } from '../tracking/tracing/did-you-forget-tracing.service';
import { selectMerchZoneViewModel } from '@order/selectors/merch-zone/merch-zone.selectors';
import { ProductsTrackingWrapper } from '../tracking/shared/models/products-tracking-wrapper';
import { ReviewOrderMerchZone } from '@shared/constants/merchandising-constants';
@Injectable({
  providedIn: 'root',
})
export class DidYouForgetService {
  constructor(
    private didYouForgetAnalytics: DidYouForgetAnalyticsService,
    private didYouForgetTracing: DidYouForgetTracingService,
    private orderItemService: OrderItemService,
    private navigationHelperService: NavigationHelperService,
    private store: Store,
    private panAppState: PanAppState,
  ) {}

  trackPageLoad(dyfVm: DyfViewModel): void {
    const productsWrapper = {
      products: dyfVm.products?.map(p => p?.product) || [],
      tracking: TrackingTypeEnum.didYouForget,
    };
    const picksProductsWrapper = {
      products: dyfVm.inspiredPicks?.products?.map(p => p?.product) || [],
      tracking: TrackingTypeEnum.inspiredPicks,
    };

    let variant: TrackingTypeEnum;
    if (dyfVm.merchZone?.variant === ReviewOrderMerchZone.topSellerVariant) {
      variant = TrackingTypeEnum.topSellers;
    } else if (
      dyfVm.merchZone?.variant === ReviewOrderMerchZone.trendingNowVariant
    ) {
      variant = TrackingTypeEnum.trendingNow;
    }
    const merchZoneProductsWrapper: ProductsTrackingWrapper = {
      products: dyfVm.merchZone?.products?.map(p => p?.product) || [],
      tracking: variant,
    };

    this.didYouForgetAnalytics.trackPageLoad(
      productsWrapper,
      picksProductsWrapper,
      merchZoneProductsWrapper,
    );
    this.didYouForgetTracing.tracePageLoad(dyfVm);
  }

  trackPageLoadForCoveoTopSellers(products): void {
    this.didYouForgetAnalytics.trackPageLoadForCoveoTopSellers(products);
  }

  getDyfProductNumbers$(): Observable<number[]> {
    return this.store.select(selectDYFProductNumbers);
  }

  loadDyfViewModel$(): Observable<DyfViewModel> {
    const dyfModel$ = this.store.select(selectDYFViewModel());
    const inspiredPicksModel$ = this.store.select(
      selectInspiredPickViewModel(),
    );
    const merchZoneModel$ = this.panAppState
      .feature$([FEATURES.split_global_review_order_merch_zone.name])
      .pipe(
        switchMap(isMerchZoneEnabled => {
          return this.store.select(
            selectMerchZoneViewModel(isMerchZoneEnabled),
          );
        }),
      );
    const minOrderMetEnabled$ = this.panAppState?.feature$([
      FEATURES.split_global_min_order_met.name,
    ]);

    return combineLatest([
      dyfModel$,
      inspiredPicksModel$,
      merchZoneModel$,
      minOrderMetEnabled$,
    ]).pipe(
      switchMap(([dyfVM, inspiredPicks, merchZone, minOrderMetEnabled]) =>
        of({ ...dyfVM, inspiredPicks, merchZone, minOrderMetEnabled }),
      ),
    );
  }

  allProductNumbers(dyfVm: DyfViewModel): number[] {
    const dyfProductNumbers = dyfVm?.products?.map(
      product => product?.product?.productNumber,
    );
    const inspiredPicksNumbers = dyfVm?.inspiredPicks?.products?.map(
      product => product?.product?.productNumber,
    );
    const merchZoneProductNumbers = dyfVm?.merchZone?.products?.map(
      product => product?.product?.productNumber,
    );

    return [
      ...new Set([
        ...(dyfProductNumbers ?? []),
        ...(inspiredPicksNumbers ?? []),
        ...(merchZoneProductNumbers ?? []),
      ]),
    ];
  }

  changeQuantity(
    productNumber: number,
    newQuantity: number,
    each: boolean,
    trackingData: Tracking,
  ) {
    this.orderItemService.changeQuantity(
      productNumber,
      newQuantity,
      each,
      trackingData,
    );
  }

  routeToPDP(productNumber: number, isQuickView: boolean, rank: number) {
    const lnksrc = isQuickView ? 'didYouForgetQuickView' : 'didYouForget';
    this.navigationHelperService.routeToProductDetailsQueryParams(
      productNumber,
      { lnksrc, merchRank: rank },
    );
  }
}
